import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { CategoryItem } from "@/components/tree/CategoryItem";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Tree = () => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedChildCategories, setExpandedChildCategories] = useState<string[]>([]);
  const [selectedUpdate, setSelectedUpdate] = useState<string>("all");

  const { data: updateOptions } = useQuery({
    queryKey: ["update-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories_atp_autoteile_de')
        .select('created_at')
        .order('created_at', { ascending: false })
        .limit(10000); // Increased limit to 10000
        
      if (error) {
        console.error('Error fetching update options:', error);
        throw error;
      }
      
      // Process the dates to create the daystring format
      const processedData = data
        .filter(item => item.created_at) // Filter out null timestamps
        .map(item => {
          const date = new Date(item.created_at);
          const hour = date.getHours().toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const year = date.getFullYear();
          
          return `${hour}h ${day}-${month}-${year}`;
        });
      
      // Remove duplicates but maintain order
      const uniqueDates = [...new Set(processedData)];
      console.log('Available updates:', uniqueDates); // Debug log
      return uniqueDates;
    },
  });

  useEffect(() => {
    if (updateOptions && updateOptions.length > 0) {
      setSelectedUpdate(updateOptions[0]);
    }
  }, [updateOptions]);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["level1-categories", selectedUpdate],
    queryFn: async () => {
      let query = supabase
        .from("categories_atp_autoteile_de")
        .select("*")
        .eq("level", 1)
        .eq("index", "index, follow")
        .limit(10000); // Added limit of 10000 rows

      if (selectedUpdate && selectedUpdate !== "all") {
        const [hour, date] = selectedUpdate.split('h ');
        const [day, month, year] = date.split('-');
        const startDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour)
        );
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        query = query
          .gte('created_at', startDate.toISOString())
          .lt('created_at', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }
      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((name) => name !== categoryName)
        : [...prev, categoryName]
    );
  };

  const toggleChildCategory = (categoryName: string) => {
    setExpandedChildCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((name) => name !== categoryName)
        : [...prev, categoryName]
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="container py-6 flex-1">
        <h1 className="text-4xl font-bold mb-8 text-center">Tree Investigation</h1>
        
        <div className="mb-6 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Update:</span>
            <Select
              value={selectedUpdate}
              onValueChange={setSelectedUpdate}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Select update time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All updates</SelectItem>
                {updateOptions?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center">Loading categories...</div>
        ) : (
          <div className="space-y-2">
            {categories?.map((category) => (
              <CategoryItem
                key={category.id}
                category={category}
                isExpanded={expandedCategories.includes(category.name)}
                expandedChildCategories={expandedChildCategories}
                onToggle={() => toggleCategory(category.name)}
                onChildToggle={toggleChildCategory}
                selectedUpdate={selectedUpdate}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Tree;